import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleCallback = async () => {
      const correlationId = logger.getCorrelationId();
      logger.info('Google OAuth callback received', { correlationId });

      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const error = params.get('error');

        if (error) {
          logger.error('Google OAuth error', new Error(error), { correlationId });
          toast({
            title: "Erro na autenticação",
            description: "Não foi possível conectar com o Google Calendar",
            variant: "destructive"
          });
          navigate('/profile');
          return;
        }

        if (!code) {
          logger.error('Missing authorization code', new Error('No code in callback'), { correlationId });
          toast({
            title: "Erro",
            description: "Código de autorização não recebido",
            variant: "destructive"
          });
          navigate('/profile');
          return;
        }

        const codeVerifier = sessionStorage.getItem('google_pkce_verifier');
        
        if (!codeVerifier) {
          logger.error('Missing code verifier', new Error('No verifier in storage'), { correlationId });
          toast({
            title: "Erro",
            description: "Sessão expirada, tente novamente",
            variant: "destructive"
          });
          navigate('/profile');
          return;
        }

        const redirectUri = `${window.location.origin}/oauth/google/callback`;
        logger.info('Invoking google-calendar-oauth-callback', { correlationId, hasCode: true, hasVerifier: true, redirectUri });

        const { data, error: invokeError } = await supabase.functions.invoke('google-calendar-oauth-callback', {
          body: { code, code_verifier: codeVerifier, redirect_uri: redirectUri }
        });

        sessionStorage.removeItem('google_pkce_verifier');

        if (invokeError) {
          logger.error('Edge function error', invokeError, { correlationId });
          
          // Send to frontend error log
          await supabase.functions.invoke('log-frontend-error', {
            body: {
              error_type: 'oauth_callback_error',
              error_message: invokeError.message,
              stack_trace: invokeError.stack || '',
              user_agent: navigator.userAgent,
              url: window.location.href,
              correlation_id: correlationId,
              metadata: { provider: 'google', step: 'edge_function_invoke' }
            }
          });

          toast({
            title: "Erro ao conectar",
            description: "Não foi possível completar a conexão com o Google Calendar",
            variant: "destructive"
          });
          navigate('/profile');
          return;
        }

        logger.info('Google Calendar connected successfully', { correlationId, responseCorrelationId: data?.correlationId });

        toast({
          title: "Google Calendar conectado!",
          description: "Sua conta foi conectada com sucesso"
        });

        navigate('/profile');

      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        logger.error('Unexpected error in Google callback', err, { correlationId });
        
        await supabase.functions.invoke('log-frontend-error', {
          body: {
            error_type: 'oauth_callback_exception',
            error_message: err.message,
            stack_trace: err.stack || '',
            user_agent: navigator.userAgent,
            url: window.location.href,
            correlation_id: correlationId,
            metadata: { provider: 'google', step: 'callback_handler' }
          }
        });

        toast({
          title: "Erro inesperado",
          description: "Ocorreu um erro ao processar o callback",
          variant: "destructive"
        });
        navigate('/profile');
      }
    };

    handleCallback();
  }, [navigate, toast]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Conectando com Google Calendar...</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
